import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { FC, MouseEvent, useState } from 'react'
import { useNotificationService } from 'src/features/notificationService/useNotificationService'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { queryKeys } from '../../api/queryKeys'
import { useMutationDeleteMaster } from '../../api/useMutationDeleteMaster'
import { useMutationUpdateMaster } from '../../api/useMutationUpdateMaster'
import { masterTemplatesQueryFn } from '../../api/useQueryMasterTemplates'
import { MASTER_NAME_MAX_LENGTH } from '../../constants'
import { MasterTemplatesDrawer } from '../MasterTemplatesDrawer/MasterTemplatesDrawer'

type Props = {
  masterUuid: string
  masterName: string
  existingNames: string[]
}

export const MasterRowActionMenu: FC<Props> = ({
  masterUuid,
  masterName,
  existingNames,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [templatesDrawerOpen, setTemplatesDrawerOpen] = useState(false)
  const [checkingTemplates, setCheckingTemplates] = useState(false)
  const [newName, setNewName] = useState(masterName)
  const [renameError, setRenameError] = useState('')
  const { showNotification } = useNotificationService()
  const queryClient = useQueryClient()
  const updateMaster = useMutationUpdateMaster(masterUuid)
  const deleteMaster = useMutationDeleteMaster()

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = () => setAnchorEl(null)

  const handleRenameOpen = (event: MouseEvent) => {
    event.stopPropagation()
    setNewName(masterName)
    setRenameError('')
    setRenameOpen(true)
    handleMenuClose()
  }

  const handleTemplatesDrawerOpen = (event: MouseEvent) => {
    event.stopPropagation()
    setTemplatesDrawerOpen(true)
    handleMenuClose()
  }

  const validateRenameName = (): boolean => {
    const trimmed = newName.trim()
    if (!trimmed) {
      setRenameError('マスタ名は必須です')
      return false
    }
    if (trimmed.length > MASTER_NAME_MAX_LENGTH) {
      setRenameError(
        `マスタ名は${MASTER_NAME_MAX_LENGTH}文字以内で入力してください`,
      )
      return false
    }
    if (trimmed !== masterName && existingNames.includes(trimmed)) {
      setRenameError('このマスタ名は既に使用されています')
      return false
    }
    setRenameError('')
    return true
  }

  const handleRenameSubmit = () => {
    if (!validateRenameName()) return
    updateMaster.mutate(
      { name: newName.trim() },
      {
        onSuccess: () => {
          showNotification('マスタ名を変更しました')
          setRenameOpen(false)
        },
        onError: (error: unknown) => {
          const message =
            isHTTPError(error) &&
            error.status === 400 &&
            error.errors.length > 0
              ? error.errors.join('\n')
              : 'マスタ名の変更に失敗しました'
          showNotification(message, { notificationType: 'error' })
        },
      },
    )
  }

  const handleDeleteOpen = async (event: MouseEvent) => {
    event.stopPropagation()
    if (checkingTemplates) return
    setCheckingTemplates(true)
    try {
      const res = await queryClient.fetchQuery({
        queryKey: queryKeys.masterTemplates(masterUuid),
        queryFn: () => masterTemplatesQueryFn(masterUuid),
      })
      handleMenuClose()
      if (res.templates.length > 0) {
        setTemplatesDrawerOpen(true)
        showNotification('ひな形で使用されているため削除できません', {
          notificationType: 'error',
        })
      } else {
        setDeleteOpen(true)
      }
    } catch {
      handleMenuClose()
      showNotification(
        '紐づき確認に失敗したため削除できません。時間をおいて再度お試しください',
        { notificationType: 'error' },
      )
    } finally {
      setCheckingTemplates(false)
    }
  }

  const handleDeleteSubmit = () => {
    deleteMaster.mutate(masterUuid, {
      onSuccess: () => {
        showNotification('マスタを削除しました')
        setDeleteOpen(false)
      },
      onError: error => {
        const errorMessage =
          isHTTPError(error) && error.status === 409 && error.errors.length > 0
            ? error.errors.join('\n')
            : 'マスタの削除に失敗しました'
        showNotification(errorMessage, { notificationType: 'error' })
      },
    })
  }

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        aria-label={`${masterName}のメニューを開く`}
        data-testid={`master-row-action-menu-${masterUuid}`}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleTemplatesDrawerOpen}>
          紐づいているひな形を確認
        </MenuItem>
        <MenuItem onClick={handleRenameOpen}>マスタ名の変更</MenuItem>
        <MenuItem
          onClick={handleDeleteOpen}
          disabled={checkingTemplates}
          sx={{ color: 'error.main' }}
        >
          削除
          {checkingTemplates && (
            <CircularProgress size={16} sx={{ ml: 1 }} color="inherit" />
          )}
        </MenuItem>
      </Menu>

      {/* 名前変更ダイアログ */}
      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        onClick={e => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>マスタ名を変更</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">マスタ名</Typography>
          <TextField
            autoFocus
            fullWidth
            value={newName}
            onChange={e => {
              setNewName(e.target.value)
              if (renameError) setRenameError('')
            }}
            error={!!renameError}
            helperText={renameError}
            sx={{ mt: 1 }}
            data-testid="master-rename-input"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} color="secondary">
            キャンセル
          </Button>
          <Tooltip
            title={!newName.trim() ? 'マスタ名を入力してください' : ''}
            arrow
          >
            <span>
              <Button
                onClick={handleRenameSubmit}
                variant="contained"
                disabled={updateMaster.isPending || !newName.trim()}
                data-testid="master-rename-submit-button"
              >
                変更
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* 紐づいているひな形ドロワー */}
      <MasterTemplatesDrawer
        open={templatesDrawerOpen}
        onClose={() => setTemplatesDrawerOpen(false)}
        masterUuid={masterUuid}
        masterName={masterName}
      />

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onClick={e => e.stopPropagation()}
      >
        <DialogTitle>マスタを削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{masterName}」を削除しますか？この操作は取り消せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} color="secondary">
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteSubmit}
            variant="contained"
            color="error"
            disabled={deleteMaster.isPending}
            data-testid="master-delete-submit-button"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
